import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
//slices
import { companySelector } from 'slices/company';
// api
import { deleteMenuCategory, editMenuCategory, createMenuCategory, getMateriasIbic } from 'api/company';
// components
import CustomInput from 'components/backoffice/customInput';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import SearchEntity from '../customInput/SearchEntity';

const MenuMattersForm = ({ categoryData, isLoading, isEditing }) => {
  const [localCategoryData, setLocalCategoryData] = useState(
    categoryData || {
      state: false,
      include_on_menu: false,
      genre_ibic: [],
    },
  );
  const navigate = useNavigate();
  const { t } = useTranslation(['backoffice', 'header']);
  const company = useSelector(companySelector);
  const company_id = company.id;
  const languages = company.languages;
  const [loading, setLoading] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [error, setError] = useState({});
  const [choices, setChoices] = useState([]);
  const [isFetchingMaterias, setIsFetchingMaterias] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(
    languages.reduce((acc, language) => ({ ...acc, [language.code_iso2]: false }), {}),
  );

  const getMaterias = async (materiasSeleccionadas = []) => {
    setIsFetchingMaterias(true);
    if (materiasSeleccionadas.length === 0 && !isEditing) {
      try {
        const { data, status } = await getMateriasIbic();
        if (data && status === 200) {
          const results = data.results.map((val) => ({
            value: val.id,
            label: (
              <span>
                <b>{val.codigo}</b> ({val.descripcion})
              </span>
            ),
          }));
          setChoices(results);
        }
      } catch (error) {
        setChoices([]);
      } finally {
        setIsFetchingMaterias(false);
      }
      return;
    }
    try {
      const allResults = [];
      for (const materia of materiasSeleccionadas) {
        const descripcion = materia.descripcion;
        const { data, status } = await getMateriasIbic({
          page: 1,
          page_size: 10,
          search: descripcion,
          fields: 'id,descripcion,codigo',
        });
        if (data && status === 200) {
          const mapped = data.results.map((val) => ({
            value: val.id,
            label: (
              <span>
                <b>{val.codigo}</b> ({val.descripcion})
              </span>
            ),
          }));
          allResults.push(...mapped);
        }
      }
      setChoices(allResults);
    } catch (error) {
      console.error('Error buscando materias por descripción:', error);
      setChoices([]);
    } finally {
      setIsFetchingMaterias(false);
    }
  };

  useEffect(() => {
    if (categoryData?.genre_ibic && categoryData.genre_ibic.length > 0) {
      const materiasValidas = categoryData.genre_ibic
        .filter((m) => m?.descripcion)
        .map((m) => ({
          descripcion: m.descripcion,
        }));
      getMaterias(materiasValidas);
    } else {
      getMaterias();
    }
  }, [categoryData?.genre_ibic]);

  const filterChoices = (genreIbic) => {
    if (!choices.length || !genreIbic.length) return [];
    return genreIbic
      .map((item) =>
        choices.find((choice) => {
          return choice.value === item.id;
        }),
      )
      .filter(Boolean);
  };

  useEffect(() => {
    if (choices.length > 0 && categoryData?.genre_ibic?.length) {
      const selectedOptions = filterChoices(categoryData.genre_ibic);
      setLocalCategoryData((prevData) => ({
        ...prevData,
        ...categoryData,
        genre_ibic: selectedOptions,
      }));
    } else if (categoryData) {
      setLocalCategoryData((prevData) => ({
        ...prevData,
        ...categoryData,
        genre_ibic: [],
      }));
    }
  }, [choices, categoryData]);

  const handleChange = (name, value) => {
    setLocalCategoryData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setError((prevError) => ({
      ...prevError,
      [name]: null,
    }));
  };

  const handleResponse = (response, isEditing) => {
    const { status } = response;
    if (status === 200 || status === 201) {
      toast.success(isEditing ? t('editadocorrectamente') : t('creadocorrectamente'));
      navigate('/intranet/menu-matters', { replace: true });
    } else {
      toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const namesFilled = languages.every((language) => localCategoryData[`name_${language.code_iso2}`]?.trim());
    try {
      setLoading(true);
      if (!namesFilled) {
        languages.forEach((language) => {
          if (!localCategoryData[`name_${language.code_iso2}`]?.trim()) {
            setError((prevError) => ({
              ...prevError,
              [`name_${language.code_iso2}`]: t('camporequerido'),
            }));
            setOpenAccordion((prevOpen) => ({
              ...prevOpen,
              [language.code_iso2]: true,
            }));
          }
        });
        return;
      }
      const transformedData = {
        ...localCategoryData,
        description: localCategoryData['description_es'],
        name: localCategoryData['name_es'],
        company_id,
        genre_ibic: localCategoryData.genre_ibic.map((option) => option.value),
      };
      const response = isEditing
        ? await editMenuCategory(categoryData.id, transformedData)
        : await createMenuCategory(transformedData);
      handleResponse(response, isEditing);
    } catch (error) {
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionToggle = (languageCode) => {
    setOpenAccordion((prevOpen) => ({
      ...prevOpen,
      [languageCode]: !prevOpen[languageCode],
    }));
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const { status } = await deleteMenuCategory(localCategoryData.id);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        navigate('/intranet/menu-matters', { replace: true });
      } else {
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="backoffice-accordion mt-5">
      <Form onSubmit={onSubmit}>
        <div className="card-faqs-container">
          <Row className="mx-1 w-100">
            {languages.map((language) => (
              <React.Fragment key={language.code_iso2}>
                <Accordion className="mt-3 card-faqs" activeKey={openAccordion[language.code_iso2] ? '0' : null}>
                  <Accordion.Item eventKey="0" className="bg-transparent border-0">
                    <Accordion.Header
                      className="text-light card-faqs-header mt-0"
                      onClick={() => handleAccordionToggle(language.code_iso2)}
                    >
                      {language.nombre}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="card-faqs-line mb-2" />
                      <div className="d-flex flex-row">
                        <div className="card-faqs-item">
                          <Form.Group className="mb-3">
                            <CustomInput
                              label={`${t('name')}`}
                              type="text"
                              name={`name_${language.code_iso2}`}
                              value={localCategoryData[`name_${language.code_iso2}`] || ''}
                              onChange={(value) => handleChange(`name_${language.code_iso2}`, value)}
                              placeholder={`${t('name')}`}
                              required
                            />
                            {error[`name_${language.code_iso2}`] && (
                              <small className="text-danger">{error[`name_${language.code_iso2}`]}</small>
                            )}
                          </Form.Group>
                        </div>
                        <div className="card-faqs-item">
                          <Form.Group className="mb-3">
                            <Form.Label>{t('description')}</Form.Label>
                            {openAccordion[language.code_iso2] && (
                              <CkEditorComponent
                                value={localCategoryData[`description_${language.code_iso2}`] || ''}
                                onChange={(content) => handleChange(`description_${language.code_iso2}`, content)}
                                placeholder={t('description')}
                              />
                            )}
                          </Form.Group>
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </React.Fragment>
            ))}
          </Row>
        </div>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0 select">
            <Form.Group>
              <SearchEntity
                fetchData={getMateriasIbic}
                filterField="descripcion"
                onSelected={(selectedItems) => {
                  const formattedValues = selectedItems.map((item) => ({
                    value: item.value,
                    label: item.label,
                  }));
                  handleChange('genre_ibic', formattedValues);
                }}
                defaultValue={localCategoryData.genre_ibic}
                placeholder={isFetchingMaterias ? t('charging') : t('ibicSubjects')}
                labelField="descripcion"
                valueField="id"
                additionalFields={['codigo']}
                showLabel={t('ibicSubjects')}
                isMulti={true}
                formatCustomLabel={(item) => (
                  <span>
                    <b>{item.codigo}</b> ({item.descripcion})
                  </span>
                )}
              />
            </Form.Group>
          </Col>
          <Col sm={12} md={12} lg={3} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <CustomCheckbox
              label={t('includeInMenu')}
              checked={localCategoryData.include_on_menu}
              onChange={(e) => handleChange('include_on_menu', e.target.checked)}
            />
          </Col>
          <Col sm={12} md={12} lg={3} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <CustomCheckbox
              label={t('state')}
              checked={localCategoryData.state}
              onChange={(e) => handleChange('state', e.target.checked)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="d-flex justify-content-end gap-3 mt-4">
              <Button
                color="primary"
                type="submit"
                className="btn-intra bg-primary-color btn btn-primary"
                loading={isLoading}
              >
                {t('save')}
                <SpinnerComponent loading={isLoading} />
              </Button>
              {isEditing && (
                <Button
                  color="secondary"
                  className="btn-intra bg-secondary-color btn btn-primary"
                  onClick={() => setOpenModalDelete(true)}
                  disabled={isLoading}
                >
                  {t('eliminar')}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Form>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deleteMenuMatters')}
        beforeTitle={t('editMenuMatters')}
      />
    </div>
  );
};

MenuMattersForm.propTypes = {
  categoryData: PropTypes.object,
  isLoading: PropTypes.bool,
  isEditing: PropTypes.bool,
};

export default MenuMattersForm;
